import { useEffect, useRef, useState } from "react";
import { Box, Button, createStyles, Flex, Loader, Skeleton, Text, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import swal from "sweetalert";
import {
  IconClock,
  IconCheck,
  IconInbox,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSettings,
} from "@tabler/icons-react";
import { GoFile } from "react-icons/go";
import { PiList } from "react-icons/pi";
import { HiOutlineViewBoards } from "react-icons/hi";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import ServiceRequestsTable from "../../../components/ServiceRequestsTable";
import ServiceRequestsBoard from "../../../components/ServiceRequestsBoard";
import ServiceRequestNewDrawer from "../../../components/ServiceRequestNewDrawer";
import ServiceRequestDetailDrawer from "../../../components/ServiceRequestDetailDrawer";
import ServiceRequestSettings from "../../../components/ServiceRequestSettings";
import ServiceRequestReportDrawer from "../../../components/ServiceRequestReportDrawer";
import ServiceRequestsStaffView from "../../../components/ServiceRequestsStaffView";
import { isServiceRequestAdmin } from "../../../utils/index";
import { useSrButtonClassNames } from "../../../components/ServiceRequestFields";
import {
  getIssueCategories,
  getTemplates,
  getBoards,
  getDashboardKpis,
  getServiceRequests,
  getServiceRequestKanban,
  moveServiceRequest,
  deleteServiceRequest,
  cancelServiceRequest,
  getServiceRequestSettings,
} from "../../../services/serviceRequests";

const useStyles = createStyles(() => ({
  bgColorClass: { width: "100%", backgroundColor: "var(--app-bg)" },
  view: {
    padding: "22px 24px 40px",
    "@media (max-width: 768px)": { padding: "16px 14px 32px" },
    "@media (max-width: 480px)": { padding: "12px 10px 28px" },
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    marginBottom: 18,
    flexWrap: "wrap",
    // On a phone the two clusters stop competing for one row: New Request takes
    // the full width (it's the primary action) and Reports/settings sit under it.
    "@media (max-width: 560px)": { alignItems: "stretch", flexDirection: "column", marginBottom: 14 },
  },
  // Reports + the settings cog. Grows to the toolbar's width once stacked, with
  // Reports taking the slack so the cog stays a 40px square.
  toolbarActions: { "@media (max-width: 560px)": { width: "100%", "& > button:first-of-type": { flex: 1 } } },
  addBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontSize: 13.5,
    fontWeight: 700,
    "&:hover": { backgroundColor: "var(--app-primary) !important", opacity: 0.85 },
    "@media (max-width: 560px)": { width: "100%" },
  },
  // Mockup's .btn-report overrides the generic .btn height (34px) to 40px.
  reportBtn: { height: 40 },
  iconSquareBtn: {
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: 10,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&:hover": { background: "var(--app-surface-2)" },
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0,1fr))",
    gap: 14,
    marginBottom: 18,
    // Three KPI cards can't hold their 28px figures side by side much below a
    // tablet, so they drop to a 2-up and then a single column.
    "@media (max-width: 900px)": { gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 },
    "@media (max-width: 560px)": { gridTemplateColumns: "1fr", gap: 10, marginBottom: 14 },
  },
  kpi: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
    "@media (max-width: 768px)": { padding: "14px 15px", gap: 8 },
  },
  kpiTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  kpiIcon: { width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0 },
  kpiIconDark: { background: "var(--app-surface-2)", color: "var(--app-text)" },
  kpiIconGreen: { background: "var(--app-tint-green)", color: "var(--app-ink-green)" },
  kpiIconAmber: { background: "var(--app-tint-amber)", color: "var(--app-ink-amber)" },
  kpiClickable: {
    position: "relative",
    cursor: "pointer",
    borderColor: "var(--app-primary)",
    transition: "border-color .12s ease, box-shadow .12s ease",
    "&:hover": { borderColor: "var(--app-primary)", boxShadow: "0 6px 18px rgba(16,24,40,.07)" },
  },
  kpiFiltering: { borderColor: "var(--app-primary)", boxShadow: "0 0 0 1px var(--app-primary)" },
  // `nowrap` is what keeps the label on one line beside its icon at desk
  // widths; on a narrow card that same rule pushes the text under the icon
  // instead, so the label and sub-line are allowed to wrap there.
  kpiLabel: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontWeight: 600,
    letterSpacing: "-0.005em",
    whiteSpace: "nowrap",
    "@media (max-width: 900px)": { whiteSpace: "normal" },
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--app-text)",
    letterSpacing: "-0.022em",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    "@media (max-width: 768px)": { fontSize: 24 },
  },
  kpiMeta: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    "@media (max-width: 900px)": { whiteSpace: "normal", display: "block" },
  },

  listToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
    "@media (max-width: 560px)": { alignItems: "stretch", flexDirection: "column", gap: 10 },
  },
  segView: {
    display: "inline-flex",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: 4,
    gap: 4,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    // Backlog/Board split the width evenly once stacked, so each stays a
    // comfortable tap target instead of shrink-wrapping its label.
    "@media (max-width: 560px)": { display: "flex" },
  },
  segBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    borderRadius: 7,
    padding: "8px 14px",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    "&:hover": { color: "var(--app-text)" },
    "@media (max-width: 560px)": { flex: 1, justifyContent: "center", padding: "9px 10px" },
  },
  segBtnOn: { background: "var(--app-text)", color: "var(--app-surface)", "&:hover": { color: "var(--app-surface)" } },

  boardEditbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    "@media (max-width: 640px)": { alignItems: "flex-start", flexDirection: "column", gap: 8 },
  },
  boardHint: { fontSize: 12.5, color: "var(--app-text-muted)" },
  boardEditToggle: { marginLeft: "auto", "@media (max-width: 640px)": { marginLeft: 0 } },

  boardSelect: { minWidth: 190 },
  searchInput: { flex: 1, minWidth: 200, "@media (max-width: 560px)": { minWidth: 0, width: "100%" } },
  searchInputControl: {
    height: 40,
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 13.5,
    borderColor: "var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&::placeholder": { color: "var(--app-text-muted)" },
    "&:focus": { borderColor: "var(--app-text)" },
  },
  searchIcon: { color: "var(--app-text-muted)" },
}));

// A KPI that paints "0" before its first response reads as real data, so the
// digits (and the sub-line that counts them) come in as skeletons instead —
// sized to the type they stand in for so the cards don't reflow on arrival.
const KpiValue = ({ classes, loading, children }) => (
  loading
    ? <Skeleton height={28} width={56} radius={6} />
    : <Text className={classes.kpiValue}>{children}</Text>
);

const KpiMeta = ({ classes, loading, children }) => (
  loading
    ? <Skeleton height={12} width={190} radius={4} />
    : <Text className={classes.kpiMeta}>{children}</Text>
);

// The backlog table and the kanban's columns should reach all the way to the
// bottom of the viewport rather than guessing a fixed height (the board used
// to hardcode `calc(100vh - 400px)`, which is wrong the moment the KPI cards
// wrap to a second row or the toolbar stacks on a phone) — this measures the
// real gap between wherever this section starts and the bottom of the
// screen, so it stays correct across breakpoints and content changes. Not a
// max height: the table should visually fill the space too, not just be
// capped by it.
const useRemainingHeight = (bottomGap, deps) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      setHeight(Math.max(240, window.innerHeight - ref.current.getBoundingClientRect().top - bottomGap));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [ref, height];
};

const ServiceRequests = () => {
  const { classes, cx } = useStyles();
  const btnClass = useSrButtonClassNames();
  const { profile } = useSelector((state) => state.profile);
  const {
    requests,
    requestsTotal,
    loading,
    boards,
    currentBoardId,
    kanban,
    kanbanLoading,
    dashboardKpis,
    kpisLoading,
    settings,
  } = useSelector((state) => state.serviceRequests);

  const location = useLocation();
  const openedFromNotificationRef = useRef(null);

  const PAGE_SIZE = 10;
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [view, setView] = useState("backlog");
  const [search, setSearch] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [boardEdit, setBoardEdit] = useState(false);
  // 40px matches `classes.view`'s own bottom padding, so the filled section's
  // bottom edge lands exactly on the viewport edge instead of forcing a few
  // more pixels of page scroll to reach that padding.
  const [fillRef, fillHeight] = useRemainingHeight(40, [view, boardEdit, kanbanLoading, loading, kpisLoading]);
  const [detailRequestId, setDetailRequestId] = useState(null);
  const [detailAutoEdit, setDetailAutoEdit] = useState(false);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportOpened, { open: openReport, close: closeReport }] = useDisclosure(false);

  const isAdmin = isServiceRequestAdmin(profile);
  const currentUserId = profile?.user?.id || profile?.user?._id;

  // The one tenant-wide board is lazily created server-side the first time
  // it's needed — getBoards() already returns it (with its columns) as a
  // single-entry array, so there's no separate per-ID fetch to keep in sync.
  const boardColumns = boards.find((b) => b.id === currentBoardId)?.columns || [];

  useEffect(() => {
    getIssueCategories();
    getTemplates();
    getBoards();
    getDashboardKpis();
    getServiceRequestSettings();
  }, []);

  // Landing here from a notification click (see NewNotificationCardComp)
  // carries the target request's id in router state — open its detail
  // drawer once. A ref (not a re-navigate to clear the state) guards
  // against reopening on every re-render, since re-navigating here raced
  // with this same effect and could drop the state before the drawer
  // ever opened.
  useEffect(() => {
    const id = location.state?.serviceRequestId;
    if (!id || openedFromNotificationRef.current === id) return;
    openedFromNotificationRef.current = id;
    setDetailRequestId(id);
    setDetailAutoEdit(false);
    openDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filters = {
    // My Queue is every ticket assigned to you, whatever stage it's in — so it
    // deliberately does NOT ask for the backlog view, which only ever returns
    // the 'backlog' and 'ready' buckets and would silently hide assigned
    // tickets sitting In Review or Awaiting Approval. Without `view` the list
    // endpoint falls through to the unrestricted query, which is also what
    // makes the "N more sign-off" badge on these rows reachable at all.
    ...(mineOnly ? {} : { view: "backlog" }),
    assignedTo: mineOnly ? currentUserId : undefined,
    search: search || undefined,
    page,
    limit: PAGE_SIZE,
  };

  // Any filter change invalidates the current page — jumping back to 1
  // avoids landing on a now-nonexistent page (e.g. page 3 of a search that
  // now only has 1 page of results).
  useEffect(() => {
    setPage(1);
  }, [mineOnly, search]);

  useEffect(() => {
    if (view !== "backlog") return;
    const t = setTimeout(() => getServiceRequests(filters), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, mineOnly, search, page]);

  useEffect(() => {
    if (view === "board") getServiceRequestKanban(filters.assignedTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, mineOnly]);

  const handleView = (row) => {
    setDetailRequestId(row.id);
    setDetailAutoEdit(false);
    openDetail();
  };

  // The drawer's own actions (cancel, approve/reject, status/assignee
  // change, save) all patch the `requests` list in place — but that patch
  // only ever updates the row's fields, never removes it, so a request that
  // just left the backlog view's ready/backlog scope (e.g. got approved)
  // lingers in the list with a status no stage in it matches: invisible in
  // every stage's rows, but still counted anywhere that reads the raw list
  // length (the "Showing N backlog" footer). The board view has the same
  // problem one level up — it reads a completely separate `kanban` slice
  // that nothing inside the drawer ever touches, so a card there kept
  // showing its old column/status until a manual refresh. Refetching
  // whichever view is open on close covers every one of those actions in
  // one place instead of threading a callback through each mutation inside
  // the drawer.
  const handleDetailClose = () => {
    closeDetail();
    if (view === "board") getServiceRequestKanban(filters.assignedTo);
    else if (view === "backlog") getServiceRequests(filters);
    getDashboardKpis(false);
  };

  const handleEdit = (row) => {
    setDetailRequestId(row.id);
    setDetailAutoEdit(true);
    openDetail();
  };

  const handleMove = (row, col) => {
    moveServiceRequest(row.id, row.columnKey, col.key).catch(() => {
      getServiceRequests(filters);
    });
  };

  const handleBoardMove = (id, fromColumnKey, toColumnKey) => {
    moveServiceRequest(id, fromColumnKey, toColumnKey).catch(() => {
      getServiceRequestKanban(filters.assignedTo);
    });
  };

  // Column add/rename/delete/reorder all commit straight to the board API
  // from within ServiceRequestsBoard; this just refreshes the two places
  // that cache the column list locally once a mutation lands.
  const handleBoardColumnsChanged = () => {
    getServiceRequestKanban(filters.assignedTo);
    getBoards();
  };

  const handleDelete = async (row) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: `Delete request "${row.title}"? This cannot be undone.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) deleteServiceRequest(row.id, () => { getServiceRequests(filters); getDashboardKpis(); });
  };

  const handleCancel = async (row) => {
    const willCancel = await swal({
      title: "Cancel this request?",
      text: "This can't be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Never mind", "Cancel Request"],
    });
    if (willCancel) cancelServiceRequest(row.id, undefined, () => getServiceRequests(filters));
  };

  // Staff (non-admin/contractor) get their own read-only-ish view — my
  // requests only, split Active/Completed — matching the mockup's v6 Staff
  // view. It reuses the same Detail/New drawers: approve/reject is already
  // gated to admins inside the detail drawer, so nothing extra to lock down.
  if (!isAdmin) {
    return (
      <Box className={classes.bgColorClass}>
        <AppNavbar title="My Service Requests" profile={profile} />
        <Box className={classes.view}>
          <ServiceRequestsStaffView />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.bgColorClass}>
      {modalOpened && (
        <ServiceRequestNewDrawer
          opened={modalOpened}
          close={closeModal}
          onCreated={() => {
            getServiceRequests(filters);
            getServiceRequestKanban();
            getDashboardKpis(false);
          }}
        />
      )}
      <ServiceRequestDetailDrawer opened={detailOpened} requestId={detailRequestId} autoEdit={detailAutoEdit} close={handleDetailClose} />
      {reportOpened && <ServiceRequestReportDrawer opened={reportOpened} close={closeReport} />}
      <AppNavbar title="Service Requests" profile={profile} />
      <Box className={classes.view}>
        {settingsOpen ? (
          <ServiceRequestSettings onBack={() => setSettingsOpen(false)} />
        ) : (
          <>
            <Box className={classes.toolbar}>
              <Button variant="filled" leftIcon={<IconPlus size={16} stroke={2.5} />} className={classes.addBtn} onClick={openModal}>
                New Request
              </Button>
              <Flex gap={10} align="center" className={classes.toolbarActions}>
                <Button leftIcon={<GoFile size={16} />} variant="default" className={cx(btnClass, classes.reportBtn)} onClick={openReport}>
                  Reports
                </Button>
                <Button
                  variant="default"
                  className={classes.iconSquareBtn}
                  onClick={() => setSettingsOpen(true)}
                  aria-label="Service Request settings"
                  title="Service Request settings"
                >
                  <IconSettings size={18} />
                </Button>
              </Flex>
            </Box>

            <Box className={classes.kpiGrid}>
              <Box className={classes.kpi}>
                <Box className={classes.kpiTop}>
                  <Text className={classes.kpiLabel}>Open Requests</Text>
                  <Box className={cx(classes.kpiIcon, classes.kpiIconDark)}><IconInbox size={18} /></Box>
                </Box>
                <KpiValue classes={classes} loading={kpisLoading}>{dashboardKpis.openRequests}</KpiValue>
                <KpiMeta classes={classes} loading={kpisLoading}>
                  {dashboardKpis.inReview} in review · {dashboardKpis.awaitingApproval} awaiting approval
                </KpiMeta>
              </Box>
              <Box className={classes.kpi}>
                <Box className={classes.kpiTop}>
                  <Text className={classes.kpiLabel}>Approved This Month</Text>
                  <Box className={cx(classes.kpiIcon, classes.kpiIconGreen)}><IconCheck size={18} /></Box>
                </Box>
                <KpiValue classes={classes} loading={kpisLoading}>{dashboardKpis.approvedThisMonth}</KpiValue>
              </Box>
              <Box
                className={cx(classes.kpi, classes.kpiClickable, mineOnly && classes.kpiFiltering)}
                onClick={() => {
                  // Turning the filter ON always resolves to the list (never
                  // the board) — the "Assigned to you" grouping
                  // ServiceRequestsTable renders for mineOnly only exists in
                  // the backlog view. Turning it back OFF just clears the
                  // filter in place, wherever the admin currently is.
                  setMineOnly((v) => {
                    if (!v) { setView("backlog"); setBoardEdit(false); }
                    return !v;
                  });
                }}
              >
                <Box className={classes.kpiTop}>
                  <Text className={classes.kpiLabel}>My Queue</Text>
                  <Box className={cx(classes.kpiIcon, classes.kpiIconAmber)}><IconClock size={18} /></Box>
                </Box>
                <KpiValue classes={classes} loading={kpisLoading}>{dashboardKpis.myQueueCount}</KpiValue>
                <Text className={classes.kpiMeta}>{mineOnly ? "Showing your tickets · click to clear" : "Tickets assigned to you"}</Text>
              </Box>
            </Box>

            <Box className={classes.listToolbar}>
              <Box className={classes.segView}>
                <button type="button" className={cx(classes.segBtn, view === "backlog" && classes.segBtnOn)} onClick={() => { setView("backlog"); setBoardEdit(false); }}>
                  <PiList size={15} />Backlog
                </button>
                <button type="button" className={cx(classes.segBtn, view === "board" && classes.segBtnOn)} onClick={() => setView("board")}>
                  <HiOutlineViewBoards size={15} />Board
                </button>
              </Box>
              <TextInput
                icon={<IconSearch size={16} />}
                placeholder="Search requests…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                className={classes.searchInput}
                classNames={{ input: classes.searchInputControl, icon: classes.searchIcon }}
              />
            </Box>

            {view === "backlog" ? (
              <Box ref={fillRef}>
                <ServiceRequestsTable
                  requests={requests}
                  loading={loading}
                  search={search}
                  mineOnly={mineOnly}
                  currentUserId={currentUserId}
                  boardColumns={boardColumns}
                  isAdmin={isAdmin}
                  allowRequesterCancel={settings.allowRequesterCancel}
                  page={page}
                  totalPages={Math.max(1, Math.ceil((requestsTotal || 0) / PAGE_SIZE))}
                  onPageChange={setPage}
                  onView={handleView}
                  onEdit={handleEdit}
                  onMove={handleMove}
                  onDelete={handleDelete}
                  onCancel={handleCancel}
                  minHeight={fillHeight}
                />
              </Box>
            ) : (
              <Box>
                <Box className={classes.boardEditbar}>
                  <Text className={classes.boardHint}>
                    {boardEdit
                      ? "Drag the handle to reorder columns · rename, add or remove columns."
                      : "Drag cards between columns to update their stage."}
                  </Text>
                  <Button
                    size="xs"
                    variant="default"
                    className={cx(btnClass, classes.boardEditToggle, boardEdit && classes.boardEditToggleDone)}
                    leftIcon={boardEdit ? undefined : <IconPencil size={14} />}
                    onClick={() => setBoardEdit((v) => !v)}
                  >
                    {boardEdit ? "Done" : "Edit board"}
                  </Button>
                </Box>
                {kanbanLoading ? (
                  <Flex justify="center" py={40}><Loader color="var(--app-primary)" /></Flex>
                ) : (
                  <Box ref={fillRef}>
                    <ServiceRequestsBoard
                      columns={kanban.columns}
                      cards={kanban.cards}
                      onCardClick={handleView}
                      onMove={handleBoardMove}
                      editMode={boardEdit}
                      boardId={currentBoardId}
                      onColumnsChanged={handleBoardColumnsChanged}
                      colHeight={fillHeight}
                    />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ServiceRequests;
